import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import Breadcrumb from "src/components/ui/Breadcrumb";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as inventoryActions from "src/redux/slices/inventorySlice";
import { formatDateTime } from "src/utils/date-time";
import { Badge } from "src/components/ui/Badge";
import { currencyFormatter, formatCreatedBy } from "src/utils/common";
import { Filter, Pencil, X } from "lucide-react";
import Button from "src/components/ui/Button";
import InventoryTransactionsFilters from "src/components/filters/InventoryTransactionsFilters";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Card from "src/components/common/card/Card";
import Modal from "src/components/ui/Modal";

const InventoryTransactionPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { transactions } = useAppSelector((state) => state.inventory);
  const [filters, setFilters] = useState<any>({
    page: 1,
    limit: 10,
  });
  const [isFiltersApplied, setIsFiltersApplied] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  // Fetch data when filters change
  useEffect(() => {
    dispatch(inventoryActions.getTransactionListRequest(filters));
  }, [filters, dispatch]);

  const handlePageChange = (page: number) =>
    setFilters((prev: any) => ({ ...prev, page }));

  const handleFiltersReset = () => {
    setIsFiltersApplied(false);
    setFilters((prev: any) => ({
      limit: 10,
      page: 1,
    }));
  };

  const handleFiltersApply = useCallback((appliedFilters: any) => {
    setIsFiltersApplied(true);
    setFilters((prev: any) => ({ ...prev, ...appliedFilters, page: 1 }));
  }, []);

  const appliedFiltersArray = [
    filters.referenceType && {
      label: "Type",
      value: filters.referenceType,
    },
    filters.productId && {
      label: "Product Name",
      value: filters.productName,
    },
    filters.createdBy && {
      label: "Created By",
      value: filters.createdByName,
    },
  ].filter(Boolean); // Remove undefined entries

  return (
    <DashboardLayout
      meta={{
        title: "Inventory Activity | Invyntra",
        description:
          "View all purchase, sale, and adjustment inventory activity.",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Inventory Activity"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Inventory Activity", path: "/app/inventory" },
          ]}
        />

        {/* Table + Filters */}
        <Card>
          <CardHeader
            title="Inventory Activity"
            description="List of all purchase, sale, and adjustment activity."
            actions={
              !isFiltersApplied && (
                <Button
                  onClick={() => setShowFiltersModal(true)}
                  // onClick={() => setShowFilters(true)}
                >
                  <Filter size={16} className="mr-1" /> Apply Filter
                </Button>
              )
            }
          />

          {isFiltersApplied && (
            <div className="flex items-center pb-4 text-sm">
              Filters:{" "}
              <p className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 rounded-full text-white px-2 pl-4 py-1 text-xs ">
                {appliedFiltersArray
                  .map(
                    (filter) =>
                      filter?.value && `${filter.label}: ${filter.value}`
                  )
                  .join(", ")}
                {/* {filters.referenceType && (
                <span>Type: {filters.referenceType}</span>
              )}
              {filters.productName && (
                <span>, Product Name: {filters.productName}</span>
              )}
              {filters.createdByName && (
                <span>, Created By: {filters.createdByName}</span>
              )} */}
                <button
                  className="px-1 py-0.5 hover:bg-primary-800 hover:text-gray-300"
                  onClick={() => setShowFiltersModal(true)}
                >
                  <Pencil size={16} />
                </button>
                <button
                  className="px-1 py-0.5 hover:bg-primary-800 hover:text-gray-300"
                  onClick={handleFiltersReset}
                >
                  <X size={16} />
                </button>
              </p>
            </div>
          )}

          <TableV2
            columns={[
              {
                key: "transactionId",
                header: "Transaction ID",
                accessor: ({ row }) => <span>#{row.transactionId}</span>,
              },
              {
                key: "referenceType",
                header: "Type",
                accessor: ({ row }) => (
                  <Badge
                    text={row.referenceType}
                    color={
                      row.referenceType === "purchase"
                        ? "green"
                        : row.referenceType === "sale"
                        ? "red"
                        : "blue"
                    }
                  />
                ),
              },
              {
                key: "referenceId",
                header: "Reference ID",
                accessor: ({ row }) => <span>#{row.referenceId}</span>,
              },
              {
                key: "productId.name",
                header: "Product",
                width: "250px",
                accessor: ({ row }) => (
                  <div>
                    <div>{row.productId?.name}</div>
                    <div className="text-xs text-gray-500">
                      <p>
                        <b>Product Id:</b> {row.productId?.productId}
                      </p>
                    </div>
                    {row.productId?.sku && (
                      <div className="text-xs text-gray-500">
                        <p>
                          <b>SKU:</b> {row.productId?.sku}
                        </p>
                      </div>
                    )}
                  </div>
                ),
              },
              { key: "quantity", header: "Qty" },
              {
                key: "unitPrice",
                header: "Unit Price",
                accessor: ({ row }) =>
                  row.unitPrice ? currencyFormatter(row.unitPrice) : "-",
              },
              {
                key: "totalPrice",
                header: "Total",
                accessor: ({ row }) =>
                  row.totalPrice ? currencyFormatter(row.totalPrice) : "-",
              },
              {
                key: "direction",
                header: "Flow",
                accessor: ({ row }) => (
                  <Badge
                    text={row.direction === "in" ? "In" : "Out"}
                    color={row.direction === "in" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                align: "center",
                accessor: ({ row }) => (
                  <div>
                    {formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A")}
                  </div>
                ),
              },
              { key: "remarks", header: "Remarks" },
            ]}
            data={transactions?.data?.data}
            pagination={transactions?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>

      {showFiltersModal && (
        <Modal
          open={showFiltersModal}
          title="Filter Inventory Activity"
          onClose={() => setShowFiltersModal(false)}
        >
          <InventoryTransactionsFilters
            selectedFilters={filters}
            onFilterApply={(appliedFilters) => {
              handleFiltersApply(appliedFilters);
              setShowFiltersModal(false);
            }}
            handleReset={() => {
              handleFiltersReset();
              setShowFiltersModal(false);
            }}
          />
        </Modal>
      )}
    </DashboardLayout>
  );
};

export default InventoryTransactionPage;
